type WithSessionStorageInput<T> = {
    /** Returned when `sessionStorage` is missing or `read` throws. */
    fallback: T;
    /** Loads a value from `sessionStorage` when the API is available. */
    read: (storage: Storage) => T;
};

/**
 * Safely reads from `sessionStorage`.
 *
 * Returns `fallback` when the Web Storage API is missing (SSR) or when `read`
 * throws (private mode, quota errors).
 *
 * @param fallback - Value returned when storage is missing or `read` throws.
 * @param read - Callback that receives `sessionStorage` and returns the stored value.
 * @returns The value from `read`, or `fallback` if storage cannot be used.
 */
export function withSessionStorage<T>({ fallback, read }: WithSessionStorageInput<T>): T {
    try {
        if (typeof sessionStorage === 'undefined') {
            return fallback;
        }

        return read(sessionStorage);
    } catch {
        return fallback;
    }
}

/**
 * Safely writes to `sessionStorage`.
 *
 * No-ops when storage is missing or `mutate` throws, so callers do not need
 * their own `typeof sessionStorage` guards.
 *
 * @param mutate - Callback that receives `sessionStorage` to set or remove keys.
 */
export function mutateSessionStorage(mutate: (storage: Storage) => void): void {
    withSessionStorage({
        fallback: undefined,
        read: storage => {
            mutate(storage);
            return undefined;
        },
    });
}
